import { useCallback, useEffect, useState } from "react";
import { Eye, Trash2 } from "lucide-react";
import FloatingInput from "../FloatingInput.jsx";
import DatePicker from "../../Common/DatePicker.jsx";
import AttachmentPreviewModal from "../../Common/AttachmentPreviewModal.jsx";
import toast from "react-hot-toast";
import { employeeService, payrollOpsService } from "../../../services/api";

function formatDisplayDate(value) {
  const s = String(value || "").slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s || "—";
  return `${m[3]}-${m[2]}-${m[1]}`;
}

function maskAccount(account) {
  const s = String(account || "");
  if (s.length <= 4) return "*".repeat(s.length);
  return `${"*".repeat(Math.max(0, s.length - 4))}${s.slice(-4)}`;
}

export function BankDetailsForm({
  employeeData,
  setEmployeeData,
  onSaveSection,
  feedback,
  darkMode,
  onNext,
  isLastTab,
  readOnly = false,
  onSaveTrigger,
  employeeId = null,
  /** Joined employees only — not onboarding / new-hire create flow */
  allowEffectiveBankHistory = false,
  /** Admin can delete history rows */
  canDeleteBankHistory = false,
}) {
  const formData = employeeData;
  const resolvedEmployeeId = employeeId || employeeData?.id || employeeData?.employeeId;

  const [ifscError, setIfscError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [showAddBank, setShowAddBank] = useState(false);
  const [bankHistory, setBankHistory] = useState([]);
  const [savingHistory, setSavingHistory] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [bankProofFile, setBankProofFile] = useState(null);
  const [applicability, setApplicability] = useState(null);
  const [confirmTyping, setConfirmTyping] = useState(false);
  const [previewState, setPreviewState] = useState({
    isOpen: false,
    url: null,
    downloadUrl: null,
    fileName: "",
  });
  const [newBank, setNewBank] = useState({
    effectiveDate: new Date().toISOString().slice(0, 10),
    bankName: "",
    accountNumber: "",
    confirmAccountNumber: "",
    ifscCode: "",
    bankBranch: "",
  });

  const canAddHistory = Boolean(allowEffectiveBankHistory && resolvedEmployeeId && !readOnly);
  const showHistoryGrid = Boolean(allowEffectiveBankHistory && resolvedEmployeeId);

  const loadBankHistory = useCallback(async () => {
    if (!resolvedEmployeeId || !allowEffectiveBankHistory) return;
    try {
      const res = await payrollOpsService.listEmployeeBankAccounts(resolvedEmployeeId);
      const rows = Array.isArray(res?.data) ? res.data : [];
      setBankHistory(rows);
      // Keep live profile fields in sync with latest row for section save / other tabs
      const latest = rows[0];
      if (latest && setEmployeeData) {
        setEmployeeData((prev) => ({
          ...prev,
          bankName: latest.bankName || prev.bankName,
          accountNumber: latest.accountNumber || prev.accountNumber,
          ifscCode: latest.ifscCode || prev.ifscCode,
          bankBranch: latest.bankBranch || prev.bankBranch,
        }));
      }
    } catch {
      setBankHistory([]);
    }
  }, [resolvedEmployeeId, allowEffectiveBankHistory, setEmployeeData]);

  const loadApplicability = useCallback(
    async (asOf) => {
      if (!resolvedEmployeeId || !allowEffectiveBankHistory) return null;
      try {
        const res = await payrollOpsService.getBankChangePreview(resolvedEmployeeId, {
          asOf: asOf || new Date().toISOString().slice(0, 10),
        });
        setApplicability(res?.data || null);
        return res?.data || null;
      } catch {
        setApplicability(null);
        return null;
      }
    },
    [resolvedEmployeeId, allowEffectiveBankHistory]
  );

  useEffect(() => {
    loadBankHistory();
  }, [loadBankHistory]);

  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      if (name === "ifscCode") {
        const cleanValue = value.toUpperCase().replace(/\s/g, "");
        if (cleanValue && !/^[A-Z0-9]+$/.test(cleanValue)) {
          setIfscError("IFSC code should contain only letters and numbers");
        } else if (cleanValue && cleanValue.length !== 11) {
          setIfscError("IFSC code must be exactly 11 characters");
        } else {
          setIfscError("");
        }
        setEmployeeData((prev) => ({ ...prev, [name]: cleanValue }));
      } else {
        setEmployeeData((prev) => ({ ...prev, [name]: value }));
      }

      let error = null;
      if (name === "bankName") error = validateBankName(value);
      else if (name === "accountNumber") error = validateAccountNumber(value);
      else if (name === "ifscCode") error = validateIFSCCode(value);
      else if (name === "bankBranch") error = validateBankBranch(value);

      setFormErrors((prev) => {
        if (error) return { ...prev, [name]: error };
        const next = { ...prev };
        delete next[name];
        return next;
      });
    },
    [setEmployeeData]
  );

  const validateBankName = (bankName) => {
    if (!bankName || bankName.trim() === "") return "Bank Name is required";
    if (bankName.trim().length < 2) return "Bank Name must be at least 2 characters long";
    if (!/^[A-Za-z\s]+$/.test(bankName.trim())) return "Bank Name can only contain letters and spaces";
    return null;
  };

  const validateAccountNumber = (accountNumber) => {
    if (!accountNumber || accountNumber.trim() === "") return "Account Number is required";
    if (accountNumber.trim().length < 5) return "Account Number must be at least 5 characters long";
    if (accountNumber.trim().length > 20) return "Account Number cannot exceed 20 characters";
    if (!/^[0-9]+$/.test(accountNumber.trim())) return "Account Number can only contain numbers";
    return null;
  };

  const validateIFSCCode = (ifscCode) => {
    if (!ifscCode || ifscCode.trim() === "") return "IFSC Code is required";
    if (ifscCode.trim().length !== 11) return "IFSC Code must be exactly 11 characters";
    if (!/^[A-Za-z0-9]+$/.test(ifscCode.trim())) return "IFSC Code should contain only letters and numbers";
    return null;
  };

  const validateBankBranch = (bankBranch) => {
    if (!bankBranch || bankBranch.trim() === "") return "Bank Branch is required";
    if (bankBranch.trim().length < 2) return "Bank Branch must be at least 2 characters long";
    if (!/^[A-Za-z\s]+$/.test(bankBranch.trim())) return "Bank Branch can only contain letters and spaces";
    return null;
  };

  const validateForm = () => {
    const errors = {};
    errors.bankName = validateBankName(formData.bankName);
    errors.accountNumber = validateAccountNumber(formData.accountNumber);
    errors.ifscCode = validateIFSCCode(formData.ifscCode);
    errors.bankBranch = validateBankBranch(formData.bankBranch);
    Object.keys(errors).forEach((key) => {
      if (errors[key] === null) delete errors[key];
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    // Joined employees: profile is driven by history grid — allow next without rewriting
    if (showHistoryGrid) {
      if (!bankHistory.length) {
        toast.error("Add bank details using Update bank details first.");
        return { saved: false };
      }
      if (onNext && !isLastTab) onNext();
      return { saved: true };
    }

    const isValid = validateForm();
    if (!isValid) {
      toast.error("Please fix the validation errors before saving.", { position: "top-right" });
      return { saved: false };
    }
    if (ifscError) {
      toast.error("Please fix the IFSC code error before saving.", { position: "top-right" });
      return { saved: false };
    }

    const sectionData = {
      bankName: formData.bankName || "",
      accountNumber: formData.accountNumber || "",
      ifscCode: formData.ifscCode || "",
      bankBranch: formData.bankBranch || "",
    };

    try {
      await onSaveSection(sectionData);
      toast.success("Bank details saved successfully", { position: "top-right" });
      if (onNext && !isLastTab) onNext();
      return { saved: true };
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to save. Please try again.";
      toast.error(msg, { position: "top-right" });
      return { saved: false };
    }
  };

  useEffect(() => {
    if (typeof onSaveTrigger === "function") onSaveTrigger(handleSave);
  }, [onSaveTrigger, handleSave]);

  const openAddBankModal = async () => {
    const today = new Date().toISOString().slice(0, 10);
    setNewBank({
      effectiveDate: today,
      bankName: "",
      accountNumber: "",
      confirmAccountNumber: "",
      ifscCode: "",
      bankBranch: "",
    });
    setBankProofFile(null);
    setConfirmTyping(false);
    await loadApplicability(today);
    setShowAddBank(true);
  };

  const openProof = async (documentId, fileName = "Bank Proof") => {
    if (!documentId) {
      toast.error("No Bank Proof found in Documents for this entry");
      return;
    }
    try {
      const [previewResp, downloadResp] = await Promise.all([
        employeeService.getEmployeeDocumentUrl(documentId),
        employeeService.getEmployeeDocumentDownloadUrl(documentId),
      ]);
      const url = previewResp?.data?.url || previewResp?.url || null;
      const downloadUrl = downloadResp?.data?.url || downloadResp?.url || null;
      if (!url) throw new Error("Missing preview url");
      setPreviewState({
        isOpen: true,
        url,
        downloadUrl: downloadUrl || url,
        fileName,
      });
    } catch (e) {
      console.error(e);
      toast.error("Failed to open Bank Proof");
    }
  };

  const handleDelete = async (row) => {
    if (!canDeleteBankHistory || !resolvedEmployeeId || !row?.id) return;
    const ok = window.confirm(
      `Delete bank entry ${row.bankName || ""} (${row.accountNumber || ""}) effective ${formatDisplayDate(row.effectiveFrom)}?`
    );
    if (!ok) return;
    setDeletingId(row.id);
    try {
      await payrollOpsService.deleteEmployeeBankAccount(resolvedEmployeeId, row.id);
      toast.success("Bank entry deleted");
      await loadBankHistory();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddBankAccount = async () => {
    if (!resolvedEmployeeId) {
      toast.error("Save employee first before updating bank history");
      return;
    }
    if (!newBank.effectiveDate) {
      toast.error("Effective Date is required");
      return;
    }
    const acctErr = validateAccountNumber(newBank.accountNumber);
    const ifscErr = validateIFSCCode(newBank.ifscCode);
    const nameErr = validateBankName(newBank.bankName);
    const branchErr = validateBankBranch(newBank.bankBranch);
    if (acctErr || ifscErr || nameErr || branchErr) {
      toast.error(acctErr || ifscErr || nameErr || branchErr);
      return;
    }
    if (newBank.accountNumber !== newBank.confirmAccountNumber) {
      toast.error("Account number and confirm account number do not match");
      return;
    }
    if (!bankProofFile) {
      toast.error("Bank Proof document is mandatory when changing bank details");
      return;
    }

    setSavingHistory(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("document", bankProofFile);
      formDataUpload.append("documentName", "Bank Proof");
      formDataUpload.append("employeeId", String(resolvedEmployeeId));
      formDataUpload.append("replaceExisting", "true");
      const uploadRes = await employeeService.uploadDocument(formDataUpload);
      const documentId = uploadRes?.documentId || uploadRes?.data?.documentId || null;

      const res = await payrollOpsService.createEmployeeBankAccount(resolvedEmployeeId, {
        autoEffective: true,
        effectiveDate: newBank.effectiveDate,
        bankName: newBank.bankName,
        accountNumber: newBank.accountNumber,
        ifscCode: newBank.ifscCode,
        bankBranch: newBank.bankBranch,
        documentId,
        requireDocument: true,
      });

      const appl = res?.applicability;
      if (res?.updatedProfile) {
        setEmployeeData((prev) => ({
          ...prev,
          bankName: newBank.bankName,
          accountNumber: newBank.accountNumber,
          ifscCode: newBank.ifscCode,
          bankBranch: newBank.bankBranch,
        }));
      }

      if (appl?.deferred) {
        toast.success(appl.message || "Bank change saved", { duration: 5000 });
        if (appl.adminHint) toast(appl.adminHint, { icon: "ℹ️", duration: 6000 });
      } else {
        toast.success("Bank details updated");
      }

      setShowAddBank(false);
      setBankProofFile(null);
      await loadBankHistory();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to add bank account");
    } finally {
      setSavingHistory(false);
    }
  };

  const border = darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]";

  return (
    <div className="space-y-4">
      {showHistoryGrid && (
        <>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <p className={`text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
              All bank accounts (including current) are listed below. First entry uses joining date by
              default. Bank Proof is stored under Documents and can be viewed with the eye icon.
            </p>
            {canAddHistory && (
              <button
                type="button"
                onClick={openAddBankModal}
                className="shrink-0 rounded border border-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--ds-primary))]"
              >
                Update bank details
              </button>
            )}
          </div>

          <div className={`overflow-auto rounded border text-xs ${border}`}>
            <table className="w-full min-w-[640px]">
              <thead className="border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]">
                <tr>
                  <th className="px-2 py-1.5 text-left">Effective Date</th>
                  <th className="px-2 py-1.5 text-left">Bank</th>
                  <th className="px-2 py-1.5 text-left">Account No</th>
                  <th className="px-2 py-1.5 text-left">IFSC</th>
                  <th className="px-2 py-1.5 text-left">Branch</th>
                  <th className="px-2 py-1.5 text-center">Proof</th>
                  {canDeleteBankHistory && !readOnly && (
                    <th className="px-2 py-1.5 text-center">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {bankHistory.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canDeleteBankHistory && !readOnly ? 7 : 6}
                      className="px-2 py-4 text-center text-[hsl(var(--ds-muted-fg))]"
                    >
                      No bank accounts yet. Use Update bank details to add one.
                    </td>
                  </tr>
                ) : (
                  bankHistory.map((row) => (
                    <tr key={row.id} className="border-t">
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {formatDisplayDate(row.effectiveFrom)}
                      </td>
                      <td className="px-2 py-1.5">{row.bankName || "—"}</td>
                      <td className="px-2 py-1.5 font-mono tabular-nums">
                        {row.accountNumber || "—"}
                      </td>
                      <td className="px-2 py-1.5">{row.ifscCode || "—"}</td>
                      <td className="px-2 py-1.5">{row.bankBranch || "—"}</td>
                      <td className="px-2 py-1.5 text-center">
                        {row.documentId || row.hasProof ? (
                          <button
                            type="button"
                            title="View Bank Proof"
                            className="inline-flex rounded p-1 text-[hsl(var(--ds-secondary))] hover:bg-[hsl(var(--ds-primary)/0.08)]"
                            onClick={() =>
                              openProof(
                                row.documentId,
                                `Bank Proof — ${formatDisplayDate(row.effectiveFrom)}`
                              )
                            }
                          >
                            <Eye size={14} />
                          </button>
                        ) : (
                          <span className="text-[hsl(var(--ds-muted-fg))]">—</span>
                        )}
                      </td>
                      {canDeleteBankHistory && !readOnly && (
                        <td className="px-2 py-1.5 text-center">
                          <button
                            type="button"
                            title="Delete entry"
                            disabled={deletingId === row.id}
                            className="inline-flex rounded p-1 text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-danger)/0.08)] disabled:opacity-50"
                            onClick={() => handleDelete(row)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Onboarding / create: classic fields only */}
      {!showHistoryGrid && (
        <fieldset disabled={readOnly} className={readOnly ? "opacity-75" : ""}>
          <div className="profile-field-grid">
            <div className="space-y-1">
              <FloatingInput
                id="bankName"
                name="bankName"
                label="Bank Name"
                value={formData.bankName || ""}
                onChange={handleInputChange}
                required
              />
              {formErrors.bankName && <div className="text-sm text-red-500">{formErrors.bankName}</div>}
            </div>
            <div className="space-y-1">
              <FloatingInput
                id="accountNumber"
                name="accountNumber"
                label="Bank Account No"
                value={formData.accountNumber || ""}
                onChange={handleInputChange}
                maxLength={20}
                required
              />
              {formErrors.accountNumber && (
                <div className="text-sm text-red-500">{formErrors.accountNumber}</div>
              )}
            </div>
            <div className="space-y-1">
              <FloatingInput
                id="ifscCode"
                name="ifscCode"
                label="IFSC Code"
                value={formData.ifscCode || ""}
                onChange={handleInputChange}
                maxLength={11}
                required
              />
              {(formErrors.ifscCode || ifscError) && (
                <div className="text-sm text-red-500">{formErrors.ifscCode || ifscError}</div>
              )}
            </div>
            <div className="space-y-1">
              <FloatingInput
                id="bankBranch"
                name="bankBranch"
                label="Branch"
                value={formData.bankBranch || ""}
                onChange={handleInputChange}
                required
              />
              {formErrors.bankBranch && (
                <div className="text-sm text-red-500">{formErrors.bankBranch}</div>
              )}
            </div>
          </div>
          {feedback && (
            <div
              className={`mt-2 text-sm ${
                feedback.type === "success"
                  ? "text-[hsl(var(--ds-success))]"
                  : "text-[hsl(var(--ds-danger))]"
              }`}
            >
              {feedback.message}
            </div>
          )}
        </fieldset>
      )}

      {showAddBank && (
        <div className="fixed inset-0 z-[10030] flex items-start justify-center bg-black/40 p-4 pt-20">
          <div
            className="max-h-[calc(100vh-6rem)] w-full max-w-md overflow-y-auto rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 shadow-lg"
          >
            <h4 className="mb-1 text-sm font-semibold">Update bank details</h4>
            <p className={`mb-3 text-[11px] ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
              Enter the new account details. Bank Proof is saved under Documents (type: Bank Proof).
            </p>

            {applicability?.deferred && (
              <div className="mb-3 rounded-[11px] border border-[hsl(var(--ds-warning)/0.4)] bg-[hsl(var(--ds-warning)/0.12)] px-3 py-2 text-[11px] text-[hsl(var(--ds-warning-fg))]">
                <p className="font-medium">{applicability.message}</p>
                {applicability.adminHint && <p className="mt-1.5">{applicability.adminHint}</p>}
              </div>
            )}

            <div className="space-y-3 text-sm">
              <div>
                <label className={`mb-1 block text-xs font-medium ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>
                  Effective Date <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  darkMode={darkMode}
                  required
                  portal
                  dropdownClassName="z-[10040]"
                  value={newBank.effectiveDate}
                  onChange={(e) => {
                    const v = e?.target?.value || "";
                    setNewBank((p) => ({ ...p, effectiveDate: v }));
                    if (v) loadApplicability(v);
                  }}
                />
              </div>
              <FloatingInput
                id="newBankName"
                name="bankName"
                label="Bank Name"
                value={newBank.bankName}
                onChange={(e) => setNewBank((p) => ({ ...p, bankName: e.target.value }))}
                required
              />
              <div onClick={() => setConfirmTyping(false)}>
                <FloatingInput
                  id="newAccountNumber"
                  name="accountNumber"
                  label="Account Number"
                  value={
                    confirmTyping && newBank.accountNumber
                      ? maskAccount(newBank.accountNumber)
                      : newBank.accountNumber
                  }
                  readOnly={confirmTyping}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 20);
                    setNewBank((p) => ({ ...p, accountNumber: v }));
                  }}
                  maxLength={20}
                  required
                />
                {confirmTyping && (
                  <p className={`-mt-3 mb-2 text-[10px] ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                    Account number is masked while confirming — click to edit
                  </p>
                )}
              </div>
              <div onFocusCapture={() => setConfirmTyping(true)}>
                <FloatingInput
                  id="confirmAccountNumber"
                  name="confirmAccountNumber"
                  label="Confirm Account Number"
                  value={newBank.confirmAccountNumber}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 20);
                    setNewBank((p) => ({ ...p, confirmAccountNumber: v }));
                  }}
                  onBlur={() => {
                    if (!newBank.confirmAccountNumber) setConfirmTyping(false);
                  }}
                  maxLength={20}
                  required
                />
              </div>
              <FloatingInput
                id="newIfsc"
                name="ifscCode"
                label="IFSC"
                value={newBank.ifscCode}
                onChange={(e) =>
                  setNewBank((p) => ({ ...p, ifscCode: e.target.value.toUpperCase() }))
                }
                required
              />
              <FloatingInput
                id="newBranch"
                name="bankBranch"
                label="Branch"
                value={newBank.bankBranch}
                onChange={(e) => setNewBank((p) => ({ ...p, bankBranch: e.target.value }))}
                required
              />
              <label className="block text-xs font-medium">
                Bank Proof document <span className="text-red-500">*</span>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,image/*,application/pdf"
                  className={`mt-1 w-full text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : ""}`}
                  onChange={(e) => setBankProofFile(e.target.files?.[0] || null)}
                />
                <span className={`mt-0.5 block text-[10px] ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                  Uploads/replaces document type &quot;Bank Proof&quot; in the Documents tab.
                </span>
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded border px-3 py-1.5 text-xs"
                disabled={savingHistory}
                onClick={() => setShowAddBank(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs text-white disabled:opacity-50"
                disabled={savingHistory}
                onClick={handleAddBankAccount}
              >
                {savingHistory ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <AttachmentPreviewModal
        isOpen={previewState.isOpen}
        url={previewState.url}
        downloadUrl={previewState.downloadUrl}
        fileName={previewState.fileName}
        onClose={() =>
          setPreviewState({ isOpen: false, url: null, downloadUrl: null, fileName: "" })
        }
      />
    </div>
  );
}
